/**
 * "Every model has its own rate into Ruby Tokens" (mockup board 1, Task 15).
 * Pure math lives in exchangeRates.ts — this just renders RateRow[].
 */
import React from 'react';
import type { AiModelDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiVibeDto } from '../../../services/aiControl/rubyAi';
import { exchangeRows } from './exchangeRateMath';
import { Card, Mono } from './ui';
import { usd } from './units';

const UNIT_LABEL: Record<string, string> = { token: 'per token', image: 'per image', second: 'per second', call: 'per call' };

/**
 * Exact Ruby Tokens, never abbreviated (no "6K", no "RT"): this card is a
 * cost-preview tool where the fraction itself is the point (4.5, not 5).
 */
const exactTokens = (n: number): string => {
  const rounded = Math.round(n * 100) / 100;
  return `${rounded.toLocaleString('en-US')} Ruby Token${rounded === 1 ? '' : 's'}`;
};

const ExchangeRates: React.FC<{ models: AiModelDto[]; actions: AiActionDto[]; vibes: AiVibeDto[] }> = ({ models, actions, vibes }) => {
  const rows = exchangeRows(models, actions, vibes);

  return (
    <Card className="overflow-hidden">
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-stone-600">No priced models yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-600">
                <th className="text-left font-semibold px-4 py-3">Model</th>
                <th className="text-left font-semibold px-3 py-3">Vendor bills by</th>
                <th className="text-left font-semibold px-3 py-3">Vendor price</th>
                <th className="text-right font-semibold px-3 py-3">1 unit at cost</th>
                <th className="text-right font-semibold px-3 py-3">×</th>
                <th className="text-right font-semibold px-3 py-3">1 unit charged as</th>
                <th className="text-left font-semibold px-3 py-3">Typical run</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.providerId}#${r.modelId}`} className="border-b border-stone-100 last:border-0 align-top">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-stone-900">{r.label}</p>
                    <Mono className="text-stone-500">{r.providerId}/{r.modelId}</Mono>
                  </td>
                  <td className="px-3 py-3 text-stone-700">{UNIT_LABEL[r.unit] ?? r.unit}</td>
                  <td className="px-3 py-3"><Mono className="text-stone-800">{r.vendorPriceText}</Mono></td>
                  <td className="px-3 py-3 text-right text-stone-800">{exactTokens(r.rubyPerUnitAtCost)}</td>
                  <td className={`px-3 py-3 text-right ${r.isDefaultMultiplier ? 'text-stone-400' : 'text-stone-700'}`}>×{r.multiplier}</td>
                  <td className="px-3 py-3 text-right font-bold text-[#9F1239]">{exactTokens(r.rubyPerUnitCharged)}</td>
                  <td className="px-3 py-3 text-stone-700">
                    {r.typical
                      ? <>{r.typical.qty.toLocaleString('en-US')} {r.typical.unitsLabel} ({usd(r.typical.costUsd)}) → <span className="font-semibold">{exactTokens(r.typical.billedTokens)}</span></>
                      : <span className="text-stone-400">No typical usage set</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default ExchangeRates;
